// src/v4/Accordion/index.tsx
import { Children, cloneElement, useState } from "react";
import { cx as cx5 } from "class-variance-authority";

// src/v4/Accordion/AccordionItem.tsx
import {
  isValidElement,
  useId
} from "react";
import { cx as cx4 } from "class-variance-authority";

// src/v4/Accordion/AccordionItemHeader.tsx
import { cx } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
function AccordionItemHeader({
  className,
  children,
  icon,
  id,
  isOpen,
  onClick
}) {
  return /* @__PURE__ */ jsx("button", {
    "aria-controls": `${id}-content`,
    "aria-expanded": isOpen,
    className: "ui-w-full ui-cursor-pointer ui-p-4 lg:ui-p-6 ui-select-none",
    "data-cy": "accordion-v4-item-header",
    id: `${id}-header`,
    onClick,
    type: "button",
    children: /* @__PURE__ */ jsxs("div", {
      className: "ui-flex ui-flex-row ui-items-center ui-justify-between",
      children: [
        /* @__PURE__ */ jsxs("div", {
          className: "ui-flex ui-w-fit ui-flex-row ui-items-center ui-justify-start ui-gap-2",
          children: [
            icon,
            /* @__PURE__ */ jsx("div", {
              className: cx(
                "ui-text-base ui-text-left ui-font-bold ui-tracking-[-2%]",
                className
              ),
              "data-cy": "accordion-v4-item-header-text",
              children
            })
          ]
        }),
        /* @__PURE__ */ jsxs("div", {
          className: "ui-relative ui-size-3",
          children: [
            /* @__PURE__ */ jsx("div", {
              className: "ui-w-full ui-h-[1.5px] ui-bg-current ui-top-1/2 ui-left-0 -ui-translate-y-1/2 ui-absolute"
            }),
            /* @__PURE__ */ jsx("div", {
              className: cx(
                "ui-h-full ui-w-[1.5px] ui-bg-current ui-left-1/2 ui-top-0 -ui-translate-x-1/2 ui-absolute ui-transition-opacity, ui-duration-200 ui-ease-in-out",
                {
                  "ui-opacity-0": isOpen,
                  "ui-opacity-100": !isOpen
                }
              ),
              "data-cy": "accordion-v4-item-close-icon"
            })
          ]
        })
      ]
    })
  });
}

// src/v4/Accordion/AccordionItemContent.tsx
import { cx as cx3 } from "class-variance-authority";
import { motion } from "framer-motion";

// src/v3/TypographyRhythm/index.tsx
import { cx as cx2 } from "class-variance-authority";
import { jsx as jsx2 } from "react/jsx-runtime";
function TypographyRhythm({
  children,
  className,
  hideOverflow = false,
  ...props
}) {
  return /* @__PURE__ */ jsx2("div", {
    className: cx2("TypographyRhythm", className, {
      hideOverflow
    }),
    "data-cy": "TypographyRhythm",
    ...props,
    children
  });
}

// src/v4/Accordion/AccordionItemContent.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var getWrapperMotionProps = ({
  duration,
  ease,
  isOpen
}) => ({
  animate: { height: isOpen ? "auto" : 0 },
  initial: { height: isOpen ? "auto" : 0 },
  transition: { duration, ease }
});
var getContentMotionProps = ({
  delay,
  duration,
  ease,
  isOpen
}) => ({
  animate: { opacity: isOpen ? 1 : 0 },
  initial: { opacity: isOpen ? 1 : 0 },
  transition: { delay, duration, ease }
});
function AccordionItemContent({
  children,
  delay,
  duration,
  ease,
  id,
  isOpen
}) {
  return /* @__PURE__ */ jsx3(motion.div, {
    "aria-hidden": !isOpen,
    "aria-labelledby": `${id}-header`,
    className: "ui-overflow-hidden -ui-translate-y-2 lg:-ui-translate-y-3",
    "data-cy": "accordion-content-wrapper",
    id: `${id}-content`,
    ...getWrapperMotionProps({ duration, ease, isOpen }),
    children: /* @__PURE__ */ jsx3(motion.div, {
      className: "ui-px-4 ui-pb-2 lg:ui-px-6 lg:ui-pb-3 ui-font-normal ui-text-sm",
      ...getContentMotionProps({ delay, duration, ease, isOpen }),
      children: /* @__PURE__ */ jsx3(TypographyRhythm, {
        className: cx3({ "ui-invisible": !isOpen }),
        hideOverflow: true,
        children
      })
    })
  });
}

// src/v4/Accordion/AccordionItem.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
function AccordionItem({
  children,
  className,
  delay = 0.2,
  duration = 0.4,
  ease = "easeInOut",
  header,
  headerClassName,
  icon,
  isOpen = false,
  onHeaderClick
}) {
  const id = useId();
  return /* @__PURE__ */ jsxs2("div", {
    className: cx4(
      "ui-rounded-lg ui-bg-gray-300/40 ui-text-sandstone-500",
      className
    ),
    "data-cy": "accordion-v4-item",
    children: [
      /* @__PURE__ */ jsx4(AccordionItemHeader, {
        className: headerClassName,
        icon,
        id,
        isOpen,
        onClick: onHeaderClick,
        children: header
      }),
      /* @__PURE__ */ jsx4(AccordionItemContent, {
        delay,
        duration,
        ease,
        id,
        isOpen,
        children
      })
    ]
  });
}
function isAccordionItem(child) {
  return isValidElement(child) && child.type === AccordionItem;
}

// src/v4/Accordion/index.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function Accordion({
  children,
  className = "",
  defaultOpenIndex = null,
  delay,
  duration,
  ease,
  headerClassName,
  onOpenChange
}) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);
  const handleClick = (idx) => {
    const next = openIndex === idx ? null : idx;
    setOpenIndex(next);
    onOpenChange == null ? void 0 : onOpenChange(next);
  };
  return /* @__PURE__ */ jsx5("div", {
    className: cx5("ui-flex ui-flex-col ui-gap-3", className),
    "data-cy": "accordion-v4",
    children: Children.map(children, (child, idx) => {
      if (isAccordionItem(child)) {
        return cloneElement(child, {
          duration,
          delay,
          ease,
          headerClassName,
          isOpen: openIndex === idx,
          onHeaderClick: () => {
            handleClick(idx);
          }
        });
      }
      return child;
    })
  });
}
export {
  Accordion,
  AccordionItem
};
//# sourceMappingURL=index.mjs.map